"use client";

import * as React from "react";

/** Tailwind's `md`. Below this the sidebar leaves the layout and becomes an offcanvas drawer. */
export const MOBILE_BREAKPOINT = 768;
/** Tailwind's `lg`. Below this an "open" sidebar is still a rail. */
export const DESKTOP_BREAKPOINT = 1024;

/**
 * Subscribes to a media query.
 *
 * Returns `false` on the server and on the first client render so SSR markup and the initial
 * hydration pass agree; the real value lands in the effect on mount. Anything that would flash
 * the wrong layout in that one frame should also be handled in CSS (see `max-md:hidden` on the
 * in-flow sidebar) rather than relying on this hook alone.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = React.useState(false);

  React.useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}

/** True below Tailwind's `md` breakpoint. */
export function useIsMobile(): boolean {
  return useMediaQuery(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
}

/** True at Tailwind's `lg` breakpoint and above. */
export function useIsDesktop(): boolean {
  return useMediaQuery(`(min-width: ${DESKTOP_BREAKPOINT}px)`);
}
